import type { PageType, SectionType } from "../types";
import type { SurfaceDepth } from "./surface-spec";
import { recommendDepth } from "./generate";

/**
 * Placement policy (P3): pick the 3–5 shopper-voice questions and the surface
 * depth from intent × page section × traffic source. Pure and deterministic —
 * the runtime bridge calls it to decide which chips to mount and how deep the
 * generated surface should go.
 */

export interface PlacementInput {
  pageType: PageType;
  seenSections: Iterable<SectionType>;
  intentScore?: number;
  topIntent?: string | null;
  acquisition?: { medium?: string | null; campaign?: string | null; source?: string | null };
  cold?: boolean;
  /**
   * Questions that have actually converted on this store (from the win store). These lead the chip
   * row: the heuristics below are an author's guess at what a shopper wants to ask, whereas these are
   * what shoppers asked before buying. Empty for a store with no wins yet → behaviour is unchanged.
   */
  provenQuestions?: string[];
  /** The storefront's language, e.g. `document.documentElement.lang`. Defaults to English. */
  locale?: string | null;
}

export interface SurfacePlacement {
  questions: string[];
  depth: SurfaceDepth;
  anchorSection: SectionType | null;
  cold: boolean;
  angle: string | null;
}

const PAID_MEDIUMS = new Set(["cpc", "ppc", "paid", "paid_social", "paidsocial", "social", "display", "ad", "ads"]);

// Order matters: the first present section drives the anchor + a targeted question.
const DECISION_SECTIONS: SectionType[] = ["buy_box", "lab_proof", "comparison", "faq", "reviews", "cart", "hero", "best_sellers"];

export function planPlacement(input: PlacementInput, options: { maxQuestions?: number } = {}): SurfacePlacement {
  const seen = new Set<SectionType>(input.seenSections);
  const angle = deriveAngle(input.acquisition?.campaign);
  const cold = input.cold ?? deriveCold(input.acquisition);
  const anchorSection = DECISION_SECTIONS.find((section) => seen.has(section)) ?? null;

  // Proven first, heuristics fill the rest.
  const candidates: string[] = [...(input.provenQuestions ?? []).map((q) => q.trim()).filter(Boolean)];
  const copy = resolveChipCopy(input.locale);
  const angleQ = angle ? copy.angle[angle as AngleKey] : null;
  if (angleQ) candidates.push(angleQ);
  for (const section of seen) {
    const q = copy.section[section];
    if (q) candidates.push(q);
  }
  if (input.topIntent) {
    const key = intentKey(input.topIntent);
    if (key) candidates.push(copy.intent[key]);
  }
  candidates.push(...(copy.page[input.pageType] ?? copy.page.default));

  const questions = dedupe(candidates).slice(0, options.maxQuestions ?? 4);
  const depth = planDepth(input, seen, cold);

  return { questions, depth, anchorSection, cold, angle };
}

function planDepth(input: PlacementInput, seen: Set<SectionType>, cold: boolean): SurfaceDepth {
  const score = input.intentScore ?? 0.5;
  if (cold) return score >= 0.5 ? 4 : 3; // cold traffic → story / full landing
  if (input.pageType === "advertorial" || input.pageType === "article") return 3;
  if (input.pageType === "cart") return 1; // tight add-on nudge at checkout
  if (seen.has("buy_box") && score >= 0.6) return 2; // warm shopper at the buy box → offer
  return recommendDepth({ entry: "ai_question", intentScore: score });
}

function deriveCold(acquisition: PlacementInput["acquisition"]): boolean {
  const medium = (acquisition?.medium ?? "").toLowerCase();
  return PAID_MEDIUMS.has(medium);
}

function deriveAngle(campaign: string | null | undefined): string | null {
  const text = (campaign ?? "").toLowerCase();
  if (!text) return null;
  if (/(value|combo|save|discount|deal|bundle)/.test(text)) return "value";
  if (/(proof|coa|lab|fake|authentic|heavy[_-]?metal|test)/.test(text)) return "proof";
  if (/(recovery|workout|gym|fitness|creatine)/.test(text)) return "recovery";
  if (/(women|woman|meno|female)/.test(text)) return "women";
  if (/(format|resin|gummies|capsule|compare)/.test(text)) return "format";
  return null;
}

/**
 * Chip copy, per language.
 *
 * These strings are SHOPPER-facing: they render on the merchant's storefront, not in our tooling.
 * They used to be Vietnamese only, with no way to ask for anything else — so an English store shipped
 * Vietnamese chips over English copy. That also contradicted the surface generator itself, whose
 * prompt already tells it to "match the store's brand voice and the shopper's language".
 *
 * The Vietnamese wording is preserved exactly; English is new and is the default, since a storefront
 * that declares no language is far likelier to be English than Vietnamese.
 */
type AngleKey = "value" | "proof" | "recovery" | "women" | "format";
type IntentKey = "value" | "proof" | "format" | "fit";

interface ChipCopy {
  angle: Record<AngleKey, string>;
  section: Partial<Record<SectionType, string>>;
  intent: Record<IntentKey, string>;
  page: Partial<Record<PageType, string[]>> & { default: string[] };
}

const COPY: Record<"en" | "vi", ChipCopy> = {
  en: {
    angle: {
      value: "How much does the 3-month bundle save?",
      proof: "How do I know this is genuine and tested?",
      recovery: "Is this good for post-workout recovery?",
      women: "Is this suitable for women?",
      format: "Which format should I choose?",
    },
    section: {
      buy_box: "Is this right for me?",
      lab_proof: "What do the lab results actually show?",
      comparison: "Compare the options for me",
      faq: "Anything else I should know before buying?",
      reviews: "What do other customers say?",
      cart: "Should I add anything to this?",
      hero: "Which product suits me best?",
      best_sellers: "Which product suits me best?",
    },
    intent: {
      value: "How much does the 3-month bundle save?",
      proof: "What do the lab results actually show?",
      format: "Compare the options for me",
      fit: "Is this right for me?",
    },
    page: {
      product: ["How much does the 3-month bundle save?", "Is this right for me?", "Compare the options for me"],
      cart: ["Should I add anything to this?", "Any deal for buying more?"],
      article: ["Which product should I start with?", "Is the evidence solid?"],
      advertorial: ["Which product should I start with?", "Is the evidence solid?"],
      default: ["Which product suits me best?", "Is there a bundle that saves money?"],
    },
  },
  vi: {
    angle: {
      value: "Combo 3 tháng tiết kiệm bao nhiêu?",
      proof: "Làm sao biết hàng thật & đã kiểm định?",
      recovery: "Có hợp phục hồi sau tập không?",
      women: "Có phù hợp với nữ không?",
      format: "Nên chọn dạng resin hay gummies?",
    },
    section: {
      buy_box: "Cái này có hợp với mình không?",
      lab_proof: "Kiểm định nói lên điều gì?",
      comparison: "So sánh các dạng giúp mình",
      faq: "Còn điều gì cần yên tâm trước khi mua?",
      reviews: "Người dùng khác thấy thế nào?",
      cart: "Có nên thêm gì cho combo không?",
      hero: "Sản phẩm nào hợp mình nhất?",
      best_sellers: "Sản phẩm nào hợp mình nhất?",
    },
    intent: {
      value: "Combo 3 tháng tiết kiệm bao nhiêu?",
      proof: "Kiểm định nói lên điều gì?",
      format: "So sánh các dạng giúp mình",
      fit: "Cái này có hợp với mình không?",
    },
    page: {
      product: ["Combo 3 tháng tiết kiệm bao nhiêu?", "Cái này có hợp với mình không?", "So sánh các dạng giúp mình"],
      cart: ["Có nên thêm gì cho combo không?", "Mua thêm được ưu đãi gì?"],
      article: ["Từ bài này nên bắt đầu với sản phẩm nào?", "Bằng chứng có đáng tin không?"],
      advertorial: ["Từ bài này nên bắt đầu với sản phẩm nào?", "Bằng chứng có đáng tin không?"],
      default: ["Sản phẩm nào hợp mình nhất?", "Có combo tiết kiệm không?"],
    },
  },
};

/** Anything Vietnamese ("vi", "vi-VN") gets the Vietnamese pack; everything else falls back to English. */
export function resolveChipCopy(locale?: string | null): ChipCopy {
  return /^vi\b/i.test((locale ?? "").trim()) ? COPY.vi : COPY.en;
}

function intentKey(topIntent: string): IntentKey | null {
  const intent = topIntent.toLowerCase();
  if (intent.includes("price") || intent.includes("value")) return "value";
  if (intent.includes("proof") || intent.includes("safety")) return "proof";
  if (intent.includes("format") || intent.includes("compare")) return "format";
  if (intent.includes("fit") || intent.includes("women") || intent.includes("recovery")) return "fit";
  return null;
}

function dedupe(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    if (!seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}
